// src/oura/components/plan/OutletNames.tsx
//
// The outlets' names (#3985): one name per outlet the contract sells.
//
// Why this screen exists at all: with 2 or more outlets the server refuses
// every per-outlet tick until the outlets are named, and nothing in the app
// offered the step. A staffer saw "Confirm the outlet names first." in red on
// the Delivery drawer with no way to do it, and five of that client's
// seventeen month-1 items were stuck.
//
// They are a CONTRACT fact, like the priority keywords beside them, so they
// live in What was sold and save with the plan through the same bar.
//
// Presentational: the draft it is given holds the names, and it decides
// nothing about saving.

import { PencilLineIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { FOCUS_RING, TYPE } from "@oura/ui";
import {
    OUTLET_ADDRESS_MAX,
    OUTLET_NAMES_FIELD_ID,
    OUTLET_NAME_MAX,
    OUTLET_PHONE_MAX,
    namesFromAnswer,
    outletDetailsChanged,
    outletNamesProblem,
    type OutletDetail,
} from "./planModel";

const FIELD = cn(
    TYPE.tb,
    "block h-11 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9",
);

export function OutletNames({
    count,
    value,
    saved,
    confirmedAt,
    confirmedBy,
    suggestions,
    filed = [],
    onChange,
    details,
    savedDetails,
    onDetails,
    disabled = false,
}: {
    /** How many outlets the contract sells. The server takes exactly this many names. */
    count: number;
    /** The draft's names, in contract order. */
    value: readonly string[];
    /** What the plan holds now, so the field can say an edit is unsaved. */
    saved: readonly string[];
    confirmedAt: string | null;
    confirmedBy: string | null;
    /** Names the newest scan found, shown as a hint on the rows they filled. */
    suggestions: readonly string[];
    /** The outlets the client named in the Setup form, [] when not sent (ourteam-backend#6407). */
    filed?: readonly string[];
    onChange: (next: string[]) => void;
    /**
     * Each outlet's address and phone as the signed quote gives them (#4776):
     * the quote's side of the Business record card. Without them it reads
     * "Not recorded" for every address and phone, whatever Google says.
     */
    details: readonly OutletDetail[];
    savedDetails: readonly OutletDetail[];
    onDetails: (next: OutletDetail[]) => void;
    disabled?: boolean;
}) {
    const problem = outletNamesProblem(value, count);
    const changed =
        JSON.stringify(value.map((n) => n.trim())) !== JSON.stringify(saved.map((n) => n.trim())) || outletDetailsChanged(details, savedDetails);

    const setDetail = (i: number, key: keyof OutletDetail, text: string) => {
        const next = Array.from({ length: count }, (_, k) => details[k] ?? { address: "", phone: "" });
        next[i] = { ...next[i], [key]: text };
        onDetails(next);
    };
    // Only a stored name is confirmed: one outlet's ticks are never gated (outletNamesConfirmed
    // is true for it), so this is the field's own test, never that one (Jack, #4594).
    const confirmed = Boolean(confirmedAt) && !problem && !changed && saved.length === count && saved.every((n) => n.trim());

    const set = (i: number, name: string) => {
        const next = Array.from({ length: count }, (_, k) => value[k] ?? "");
        next[i] = name;
        onChange(next);
    };

    // The client's answer, as the rows would be after using it; offered only when it would change them.
    const fromAnswer = filed.length ? namesFromAnswer(value, filed) : null;
    const offerAnswer = fromAnswer !== null && JSON.stringify(fromAnswer.map((n) => n.trim())) !== JSON.stringify(value.map((n) => n.trim()));

    return (
        <div className="min-w-0" id={OUTLET_NAMES_FIELD_ID} tabIndex={-1}>
            <p className={cn(TYPE.lab, "m-0")}>Outlet names</p>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                {/* The consequence, not the rule: a staffer reads this after a tick was refused. */}
                {count === 1
                    ? "The outlet's name, which its GBP Pack is named after. A GBP Pack cannot be made until it is named."
                    : `One name per outlet. A task for a single outlet cannot be ticked until all ${count} are named.`}{" "}
                The address and phone as the quote gives them are checked against the Google profile in Business record.
            </p>

            <ol className="m-0 mt-2 list-plain space-y-2 p-0">
                {Array.from({ length: count }, (_, i) => {
                    const id = `${OUTLET_NAMES_FIELD_ID}-${i}`;
                    const hint = suggestions[i]?.trim();
                    const typed = (value[i] ?? "").trim();
                    const detail = details[i] ?? { address: "", phone: "" };
                    return (
                        <li key={i}>
                        <div className="flex flex-wrap items-center gap-2">
                            <label htmlFor={id} className={cn(TYPE.tc, "w-16 flex-shrink-0 text-gray-500")}>
                                Outlet {i + 1}
                            </label>
                            <input
                                id={id}
                                value={value[i] ?? ""}
                                disabled={disabled}
                                maxLength={OUTLET_NAME_MAX}
                                autoComplete="off"
                                placeholder={hint || "Such as Mont Kiara"}
                                onChange={(e) => set(i, e.target.value)}
                                className={cn(FIELD, "min-w-0 flex-1 sm:max-w-[18rem]")}
                            />
                            {/* Offered, never filled in silently: a scan that found 2 branches for a
                                5-outlet contract must not look like 5 answered rows. */}
                            {hint && typed !== hint && (
                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => set(i, hint)}
                                    className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-blue-700 underline decoration-blue-300 underline-offset-2 hover:bg-blue-50 disabled:opacity-40", FOCUS_RING)}
                                >
                                    <PencilLineIcon aria-hidden size={12} /> Use {hint}
                                </button>
                            )}
                        </div>
                        {/* The quote's side of the Business record card, under the name it belongs to. */}
                        <div className="mt-1.5 grid gap-2 sm:ml-[4.5rem] sm:max-w-[30rem] sm:grid-cols-[minmax(0,1fr)_10rem]">
                            <input
                                id={`${id}-address`}
                                aria-label={`Outlet ${i + 1} address on the quote`}
                                value={detail.address}
                                disabled={disabled}
                                maxLength={OUTLET_ADDRESS_MAX}
                                autoComplete="off"
                                placeholder="Address on the quote"
                                onChange={(e) => setDetail(i, "address", e.target.value)}
                                className={cn(FIELD, "min-w-0")}
                            />
                            <input
                                id={`${id}-phone`}
                                aria-label={`Outlet ${i + 1} phone on the quote`}
                                value={detail.phone}
                                disabled={disabled}
                                maxLength={OUTLET_PHONE_MAX}
                                autoComplete="off"
                                inputMode="tel"
                                placeholder="Phone on the quote"
                                onChange={(e) => setDetail(i, "phone", e.target.value)}
                                className={cn(FIELD, "min-w-0")}
                            />
                        </div>
                        </li>
                    );
                })}
            </ol>

            {offerAnswer && fromAnswer && (
                // A draft, never a save: named rows keep their place, so what was ticked
                // for outlet 1 stays with outlet 1 (ourteam-backend#6407).
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(fromAnswer)}
                    className={cn(TYPE.tc, "mt-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-blue-700 underline decoration-blue-300 underline-offset-2 hover:bg-blue-50 disabled:opacity-40", FOCUS_RING)}
                >
                    <PencilLineIcon aria-hidden size={12} /> Use the client&apos;s answer: {filed.length} {filed.length === 1 ? "outlet" : "outlets"}
                </button>
            )}

            <p className={cn(TYPE.tc, "m-0 mt-1.5", problem ? "text-amber-700" : "text-gray-500")} role={problem ? "status" : undefined}>
                {problem ??
                    (changed
                        ? "Not saved yet."
                        : confirmed
                            ? `Confirmed${confirmedBy ? ` by ${confirmedBy}` : ""}. ${count === 1 ? "GBP Packs are named after it." : "Per-outlet tasks can be ticked."}`
                            : "Save the plan to confirm them.")}
            </p>
        </div>
    );
}
